/**
 * SupportDashboard Page
 *
 * Main support dashboard providing queue overview, statistics, and quick actions.
 * Central hub for customer support operations.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-149 - Support Dashboard (26 SP)
 */

import React, { useState } from 'react';
import { useNavigate, useLoaderData } from 'react-router-dom';
import {
  Activity,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { useSupportQueue, useQueueStats } from '../../hooks/support/useSupportQueue';
import { QueueTable } from '../../components/support/QueueTable';
import { StatsWidget } from '../../components/support/StatsWidget';
import DashboardLayout from '../../components/layout/DashboardLayout';

export function SupportDashboard() {
  const navigate = useNavigate();
  const loaderData = useLoaderData();
  const _user = loaderData?._user || { name: 'User', email: '_user@example.com', avatar: null };

  // Determine _user role - admins access via admin dashboard, support staff may have different role
  const userRole = _user.roles?.includes('super_admin') || _user.roles?.includes('staff') ? 'admin' : 'support';
  const activeNavId = userRole === 'admin' ? 'support' : 'dashboard';

  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    limit: 50,
    offset: 0,
  });

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const { queue, loading: queueLoading, error: queueError, refresh: refreshQueue } = useSupportQueue(filters);
  const { stats, loading: statsLoading, refresh: refreshStats } = useQueueStats();

  const handleRefresh = () => {
    refreshQueue();
    refreshStats();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0, // Reset to first page when filters change
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      limit: 50,
      offset: 0,
    });
  };

  const hasActiveFilters = filters.status || filters.priority;

  return (
    <DashboardLayout
      userRole={userRole}
      activeNavId={activeNavId}
      onNavigate={handleNavigate}
      _user={_user}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
              Support Queue
            </h1>
            <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
              Manage support requests and help customers
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/support/search/users')}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-primary)',
                backgroundColor: 'var(--color-white)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)'
              }}
            >
              Search Users
            </button>
            <button
              onClick={() => navigate('/support/search/bookings')}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-primary)',
                backgroundColor: 'var(--color-white)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)'
              }}
            >
              Search Bookings
            </button>
            <button
              onClick={handleRefresh}
              disabled={queueLoading || statsLoading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-4)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-white)',
                backgroundColor: (queueLoading || statsLoading) ? 'var(--color-gray-300)' : 'var(--color-primary)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: (queueLoading || statsLoading) ? 'not-allowed' : 'pointer',
                transition: 'all var(--transition-fast)'
              }}
            >
              <RefreshCw style={{ width: '16px', height: '16px', animation: (queueLoading || statsLoading) ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div>
        {/* Error Display */}
        {queueError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-semibold mb-2">Error Loading Queue</h3>
            <p className="text-red-600 text-sm">{queueError}</p>
            <button
              onClick={refreshQueue}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Statistics */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Queue Statistics</h2>
          <StatsWidget stats={stats} loading={statsLoading} />
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Limit Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Results per page
              </label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Queue Table */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Support Queue
            </h2>
            {queue?.pagination && (
              <span className="text-sm text-gray-600">
                {queue.pagination.total} total requests
              </span>
            )}
          </div>
          <QueueTable
            queue={queue}
            loading={queueLoading}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default SupportDashboard;
